import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  QrCode,
  Smartphone,
  Store,
  LayoutDashboard,
  BadgeCheck,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const WHATSAPP_URL =
  "https://wa.me/905533158324?text=Merhaba%2C%20Web%20Men%C3%BC%20hizmetiniz%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum.%20%C4%B0%C5%9Fletmem%20i%C3%A7in%20detayl%C4%B1%20bilgi%20alabilir%20miyim%3F";

const previewCategories = [
  "Öne Çıkanlar",
  "Ürünler",
  "Hizmetler",
  "Kampanyalar",
];

const previewItems = [
  {
    eyebrow: "ÜRÜN",
    name: "Yeni Sezon Koleksiyonu",
    desc: "Ürünlerinizi görsel, açıklama ve fiyat bilgileriyle profesyonelce sergileyin.",
    value: "₺4.850",
  },
  {
    eyebrow: "HİZMET",
    name: "Özel Tasarım Hizmeti",
    desc: "Hizmetlerinizi anlaşılır içeriklerle ziyaretçilerinize tek sayfada sunun.",
    value: "Teklif Al",
  },
  {
    eyebrow: "KAMPANYA",
    name: "Eylül Fırsatı",
    desc: "Öne çıkarmak istediğiniz kampanyaları vitrinin en görünür alanına taşıyın.",
    value: "%20",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  const openWhatsapp = () =>
    window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white scroll-smooth">
      <nav className="sticky top-0 z-30 border-b border-zinc-200/70 bg-white/90 backdrop-blur-xl lg:fixed lg:inset-x-0 lg:top-0 lg:z-50">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => scrollToSection("top")}
            className="flex min-w-0 items-center gap-3"
            aria-label="Web Menü ana sayfa"
          >
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[14px] bg-[#EA580C] text-white shadow-[0_10px_30px_rgba(234,88,12,0.22)]">
              <Menu className="h-5 w-5" />
            </div>

            <div className="min-w-0 text-left leading-tight">
              <div className="truncate text-[15px] font-extrabold tracking-[-0.02em] text-zinc-950">
                Web Menü
              </div>
              <div className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                Megriva teknolojisiyle
              </div>
            </div>
          </button>

          <div className="hidden items-center gap-1 lg:flex">
            <button
              type="button"
              onClick={() => scrollToSection("top")}
              className="rounded-full px-4 py-2 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-100 hover:text-zinc-950"
            >
              Web Menü
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("how-it-works")}
              className="rounded-full px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950"
            >
              Nasıl Çalışır
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("features")}
              className="rounded-full px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950"
            >
              Özellikler
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("pricing")}
              className="rounded-full px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950"
            >
              Fiyatlar
            </button>
          </div>

          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate("/login")}
              className="hidden rounded-full px-4 font-semibold text-zinc-700 hover:bg-zinc-100 sm:inline-flex"
              data-testid="landing-login-button"
            >
              Giriş Yap
            </Button>

            <Button
              onClick={() => navigate("/register")}
              className="rounded-full bg-zinc-950 px-5 font-semibold text-white shadow-sm hover:bg-zinc-800"
              data-testid="landing-register-button"
            >
              Hemen Başla
            </Button>
          </div>
        </div>
      </nav>

      <div aria-hidden="true" className="hidden h-[72px] lg:block" />

      <section id="top" className="relative overflow-hidden bg-[#FCFBF9]">
        <div
          aria-hidden="true"
          className="absolute left-[-180px] top-[-160px] h-[420px] w-[420px] rounded-full bg-orange-200/35 blur-[110px]"
        />
        <div
          aria-hidden="true"
          className="absolute right-[-160px] top-[80px] h-[460px] w-[460px] rounded-full bg-amber-100/70 blur-[120px]"
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-white to-transparent"
        />

        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-16 sm:px-6 sm:pt-20 lg:px-8 lg:pb-28 lg:pt-24">
          <div className="grid items-center gap-14 lg:grid-cols-[0.92fr_1.08fr] lg:gap-10">
            <div className="mx-auto max-w-2xl text-center lg:mx-0 lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-4 py-2 text-xs font-bold text-orange-700 shadow-sm">
                <BadgeCheck className="h-4 w-4" />
                İşletmenizin dijital vitrini
              </div>

              <h1
                className="mt-7 text-[38px] font-extrabold leading-[1.04] tracking-[-0.045em] text-zinc-950 sm:text-5xl lg:text-[68px]"
                data-testid="hero-heading"
              >
                İşletmenizi tek bağlantıda{" "}
                <span className="text-[#EA580C]">daha profesyonel</span>{" "}
                gösterin.
              </h1>

              <p className="mx-auto mt-6 max-w-xl text-base font-medium leading-8 text-zinc-600 sm:text-lg lg:mx-0">
                Ürünlerinizi, hizmetlerinizi, kampanyalarınızı ve işletme
                bilgilerinizi modern bir dijital vitrinde bir araya getirin.
                QR kodunuzu veya Web Menü bağlantınızı paylaşın; içeriğinizi
                dilediğiniz an güncelleyin.
              </p>

              <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
                <Button
                  size="lg"
                  onClick={() => navigate("/register")}
                  className="h-[52px] rounded-full bg-[#EA580C] px-7 text-[15px] font-bold text-white shadow-[0_14px_34px_rgba(234,88,12,0.23)] hover:bg-[#C2410C]"
                  data-testid="hero-get-started-button"
                >
                  Web Menü'nü Oluştur
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection("how-it-works")}
                  className="h-[52px] rounded-full border-zinc-300 bg-white px-7 text-[15px] font-bold text-zinc-800 shadow-sm hover:bg-zinc-50"
                  data-testid="hero-view-demo-button"
                >
                  Nasıl Çalıştığını Gör
                </Button>
              </div>

              <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-3 text-xs font-semibold text-zinc-500 lg:justify-start">
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
                  Tüm özellikler tek yerde
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
                  QR kod + özel bağlantı
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
                  Mobil uyumlu yönetim
                </span>
              </div>

              <div className="mt-10 border-t border-zinc-200 pt-6">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-400">
                  Sektör fark etmeksizin kullanın
                </p>

                <div className="mt-3 flex flex-wrap justify-center gap-2 lg:justify-start">
                  {["Kafe & Restoran", "Mobilya", "Güzellik", "Oto", "Üretim"].map(
                    (sector) => (
                      <span
                        key={sector}
                        className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-600"
                      >
                        {sector}
                      </span>
                    ),
                  )}
                </div>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-[650px]">
              <div
                aria-hidden="true"
                className="absolute left-1/2 top-1/2 h-[72%] w-[82%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-300/20 blur-[80px]"
              />

              <div className="relative rounded-[34px] border border-zinc-200/80 bg-white p-3 shadow-[0_35px_90px_rgba(24,24,27,0.12)] sm:p-4">
                <div className="flex items-center justify-between border-b border-zinc-100 px-2 pb-3">
                  <div className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                    <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                    <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  </div>

                  <div className="rounded-full bg-zinc-100 px-4 py-1.5 text-[10px] font-semibold text-zinc-500">
                    webmenu.megriva.com/arven
                  </div>

                  <div className="h-2.5 w-10 rounded-full bg-zinc-100" />
                </div>

                <div className="grid min-h-[540px] overflow-hidden rounded-[26px] bg-[#F7F7F5] sm:grid-cols-[0.33fr_0.67fr]">
                  <aside className="hidden border-r border-zinc-200/70 bg-white p-5 sm:block">
                    <div className="flex items-center gap-2.5">
                      <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#EA580C] text-xs font-black text-white">
                        WM
                      </div>
                      <div>
                        <div className="text-xs font-extrabold text-zinc-900">
                          Web Menü
                        </div>
                        <div className="mt-0.5 text-[9px] font-semibold text-zinc-400">
                          Dijital vitrin
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 space-y-2">
                      {[
                        ["01", "Ana Sayfa"],
                        ["02", "Ürünler"],
                        ["03", "Hizmetler"],
                        ["04", "Kampanyalar"],
                        ["05", "İletişim"],
                      ].map(([number, label], index) => (
                        <div
                          key={label}
                          className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-[11px] font-bold ${
                            index === 0
                              ? "bg-orange-50 text-orange-700"
                              : "text-zinc-500"
                          }`}
                        >
                          <span className="text-[9px] opacity-60">{number}</span>
                          {label}
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 rounded-2xl bg-zinc-950 p-4 text-white">
                      <QrCode className="h-5 w-5 text-orange-400" />
                      <p className="mt-3 text-[10px] font-extrabold">
                        QR ile paylaş
                      </p>
                      <p className="mt-1 text-[9px] leading-4 text-zinc-400">
                        Tek kod, her zaman güncel içerik.
                      </p>
                    </div>
                  </aside>

                  <div className="relative flex items-center justify-center overflow-hidden px-4 py-6 sm:px-6">
                    <div
                      aria-hidden="true"
                      className="absolute -right-16 top-6 h-48 w-48 rounded-full bg-orange-100 blur-2xl"
                    />

                    <div className="relative w-[286px] rounded-[38px] bg-zinc-950 p-[7px] shadow-[0_28px_60px_rgba(24,24,27,0.20)]">
                      <div className="overflow-hidden rounded-[31px] bg-white">
                        <div className="flex h-7 items-center justify-center bg-zinc-950">
                          <div className="h-1.5 w-14 rounded-full bg-zinc-700" />
                        </div>

                        <div className="max-h-[490px] overflow-hidden">
                          <div className="bg-gradient-to-br from-[#2B211C] via-[#53372B] to-[#A84D21] px-5 pb-5 pt-6 text-white">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/55">
                                  Dijital vitrin
                                </p>
                                <h3 className="mt-2 text-xl font-extrabold tracking-[-0.02em]">
                                  Arven Concept
                                </h3>
                                <p className="mt-1 text-[10px] font-medium text-white/70">
                                  Mobilya & iç mekan çözümleri
                                </p>
                              </div>

                              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-white/15 text-xs font-black backdrop-blur">
                                AC
                              </div>
                            </div>

                            <div className="mt-5 rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur">
                              <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-orange-200">
                                Öne çıkan
                              </p>
                              <p className="mt-1.5 text-xs font-extrabold">
                                Yeni sezon koleksiyonu yayında
                              </p>
                            </div>
                          </div>

                          <div className="border-b border-zinc-100 bg-white px-4 py-3">
                            <div className="no-scrollbar flex gap-2 overflow-x-auto">
                              {previewCategories.map((category, index) => (
                                <span
                                  key={category}
                                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[9px] font-bold ${
                                    index === 0
                                      ? "bg-zinc-950 text-white"
                                      : "bg-zinc-100 text-zinc-500"
                                  }`}
                                >
                                  {category}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="space-y-2.5 bg-[#FAFAF9] p-3.5">
                            {previewItems.map((item, index) => (
                              <div
                                key={item.name}
                                className="rounded-2xl border border-zinc-200/80 bg-white p-3 shadow-[0_5px_18px_rgba(24,24,27,0.04)]"
                              >
                                <div className="flex gap-3">
                                  <div
                                    className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl text-xs font-black ${
                                      index === 0
                                        ? "bg-orange-100 text-orange-700"
                                        : index === 1
                                          ? "bg-zinc-100 text-zinc-700"
                                          : "bg-amber-100 text-amber-700"
                                    }`}
                                  >
                                    0{index + 1}
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className="text-[8px] font-extrabold tracking-[0.12em] text-zinc-400">
                                          {item.eyebrow}
                                        </p>
                                        <p className="mt-1 truncate text-[11px] font-extrabold text-zinc-900">
                                          {item.name}
                                        </p>
                                      </div>

                                      <span className="shrink-0 text-[10px] font-extrabold text-[#EA580C]">
                                        {item.value}
                                      </span>
                                    </div>

                                    <p className="mt-1 line-clamp-2 text-[9px] leading-4 text-zinc-500">
                                      {item.desc}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="border-t border-zinc-100 bg-white px-4 py-3">
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] font-bold text-zinc-400">
                              Powered by Web Menü
                            </span>
                            <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="absolute bottom-8 left-3 hidden rounded-2xl border border-zinc-200 bg-white p-3 shadow-xl xl:block">
                      <div className="flex items-center gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-xl bg-orange-50 text-[#EA580C]">
                          <QrCode className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                            Paylaşım
                          </p>
                          <p className="mt-1 text-[10px] font-extrabold text-zinc-800">
                            QR veya bağlantı
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="absolute right-3 top-10 hidden rounded-2xl border border-zinc-200 bg-white p-3 shadow-xl xl:block">
                      <div className="flex items-center gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-xl bg-zinc-950 text-white">
                          <Smartphone className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                            Yönetim
                          </p>
                          <p className="mt-1 text-[10px] font-extrabold text-zinc-800">
                            Her zaman cebinizde
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative mx-auto -mt-5 flex w-[88%] flex-wrap items-center justify-center gap-x-6 gap-y-2 rounded-2xl border border-zinc-200 bg-white px-5 py-4 shadow-lg">
                <span className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-zinc-400">
                  Tek panelden
                </span>
                <span className="text-xs font-bold text-zinc-700">Ürün</span>
                <span className="h-1 w-1 rounded-full bg-zinc-300" />
                <span className="text-xs font-bold text-zinc-700">Hizmet</span>
                <span className="h-1 w-1 rounded-full bg-zinc-300" />
                <span className="text-xs font-bold text-zinc-700">Kampanya</span>
                <span className="h-1 w-1 rounded-full bg-zinc-300" />
                <span className="text-xs font-bold text-zinc-700">QR</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="scroll-mt-24 bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold text-orange-700">
                <LayoutDashboard className="h-4 w-4" />
                Sade kurulum, kolay yönetim
              </div>

              <h2 className="mt-5 max-w-2xl text-3xl font-extrabold tracking-[-0.035em] text-zinc-950 sm:text-5xl">
                Web Menü’nüz dört adımda hazır.
              </h2>
            </div>

            <p className="max-w-2xl text-base font-medium leading-8 text-zinc-600 lg:justify-self-end">
              Teknik kurulumla uğraşmadan hesabınızı oluşturun, işletmenizi
              tanımlayın, içeriklerinizi ekleyin ve Web Menü bağlantınızı
              müşterilerinizle paylaşın.
            </p>
          </div>

          <div className="mt-12 grid overflow-hidden rounded-[32px] border border-zinc-200 bg-[#FBFAF8] sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                number: "01",
                icon: Store,
                title: "Hesabını oluştur",
                desc: "Web Menü hesabını aç ve işletme bilgilerini tanımla.",
              },
              {
                number: "02",
                icon: LayoutDashboard,
                title: "Vitrinini düzenle",
                desc: "Ürün, hizmet, kategori ve işletme içeriklerini tek panelden yönet.",
              },
              {
                number: "03",
                icon: Menu,
                title: "Kampanyanı öne çıkar",
                desc: "Yeni ürünleri ve kampanyaları müşterilerinin karşısına çıkar.",
              },
              {
                number: "04",
                icon: QrCode,
                title: "QR veya link ile paylaş",
                desc: "Tek bağlantıyı sosyal medyada, mağazada veya QR kodla paylaş.",
              },
            ].map((step, index) => {
              const Icon = step.icon;

              return (
                <article
                  key={step.number}
                  className={`group relative p-6 transition sm:p-7 ${
                    index !== 3
                      ? "border-b border-zinc-200 sm:border-b-0 sm:border-r"
                      : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white text-[#EA580C] shadow-sm ring-1 ring-zinc-200">
                      <Icon className="h-5 w-5" />
                    </div>

                    <span className="text-4xl font-black tracking-[-0.05em] text-zinc-200 transition group-hover:text-orange-200">
                      {step.number}
                    </span>
                  </div>

                  <h3 className="mt-6 text-lg font-extrabold text-zinc-950">
                    {step.title}
                  </h3>

                  <p className="mt-2 text-sm font-medium leading-6 text-zinc-500">
                    {step.desc}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="features" className="scroll-mt-24 bg-[#F6F5F2] py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-xs font-bold text-zinc-600 shadow-sm">
              <BadgeCheck className="h-4 w-4 text-[#EA580C]" />
              Tüm özellikler her abonelikte açık
            </div>

            <h2 className="mt-5 text-3xl font-extrabold tracking-[-0.035em] text-zinc-950 sm:text-5xl">
              İşletmenizi dijitalde göstermek için ihtiyacınız olan temel
              araçlar tek yerde.
            </h2>

            <p className="mt-5 text-base font-medium leading-8 text-zinc-600">
              Paket farkı yok. Hangi süreyi seçerseniz seçin Web Menü’nün mevcut
              tüm özelliklerini kullanabilirsiniz.
            </p>
          </div>

          <div className="mt-12 grid gap-5 lg:grid-cols-12">
            <article className="relative overflow-hidden rounded-[30px] bg-zinc-950 p-7 text-white lg:col-span-7 lg:p-9">
              <div
                aria-hidden="true"
                className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-orange-500/20 blur-[70px]"
              />

              <div className="relative max-w-xl">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10 text-orange-300">
                  <Menu className="h-5 w-5" />
                </div>

                <h3 className="mt-6 text-2xl font-extrabold tracking-[-0.025em] sm:text-3xl">
                  Ürün ve hizmetlerinizi kendi dijital vitrininizde sergileyin.
                </h3>

                <p className="mt-4 text-sm font-medium leading-7 text-zinc-300 sm:text-base">
                  Kategori, ürün, hizmet, fiyat ve açıklamalarınızı tek panelden
                  düzenleyin. İçerik değiştiğinde yeni QR kod basmanız gerekmez.
                </p>

                <div className="mt-8 grid gap-3 sm:grid-cols-3">
                  {["Ürünler", "Hizmetler", "Kategoriler"].map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-bold text-white/90"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </article>

            <article className="rounded-[30px] border border-zinc-200 bg-white p-7 shadow-sm lg:col-span-5 lg:p-9">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-orange-50 text-[#EA580C]">
                <QrCode className="h-5 w-5" />
              </div>

              <h3 className="mt-6 text-2xl font-extrabold tracking-[-0.025em] text-zinc-950">
                Tek QR, her zaman güncel.
              </h3>

              <p className="mt-4 text-sm font-medium leading-7 text-zinc-600">
                Web Menü bağlantınızı QR kodla, sosyal medya biyografinizde veya
                mesajlaşma uygulamalarında paylaşın. İçeriği siz güncelleyin,
                bağlantı aynı kalsın.
              </p>

              <div className="mt-8 flex items-center gap-3 rounded-2xl bg-zinc-50 p-4">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-white text-zinc-950 shadow-sm">
                  <QrCode className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                    Paylaşım
                  </p>
                  <p className="mt-1 text-sm font-extrabold text-zinc-900">
                    QR kod + özel bağlantı
                  </p>
                </div>
              </div>
            </article>

            <article className="rounded-[30px] border border-zinc-200 bg-white p-7 shadow-sm lg:col-span-4">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-zinc-950 text-white">
                <Smartphone className="h-5 w-5" />
              </div>

              <h3 className="mt-5 text-xl font-extrabold text-zinc-950">
                Mobil uyumlu vitrin
              </h3>

              <p className="mt-3 text-sm font-medium leading-7 text-zinc-600">
                Web Menü’nüz telefon, tablet ve masaüstünde sade ve okunabilir
                bir deneyim sunar.
              </p>
            </article>

            <article className="rounded-[30px] border border-zinc-200 bg-white p-7 shadow-sm lg:col-span-4">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-50 text-[#EA580C]">
                <LayoutDashboard className="h-5 w-5" />
              </div>

              <h3 className="mt-5 text-xl font-extrabold text-zinc-950">
                Kolay yönetim
              </h3>

              <p className="mt-3 text-sm font-medium leading-7 text-zinc-600">
                İçeriklerinizi web panelinden ve mobil deneyiminizden hızlıca
                yönetin; değişiklikleri tek yerde tutun.
              </p>
            </article>

            <article className="rounded-[30px] border border-zinc-200 bg-white p-7 shadow-sm lg:col-span-4">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-amber-50 text-amber-700">
                <BadgeCheck className="h-5 w-5" />
              </div>

              <h3 className="mt-5 text-xl font-extrabold text-zinc-950">
                Kampanyaları öne çıkarın
              </h3>

              <p className="mt-3 text-sm font-medium leading-7 text-zinc-600">
                Duyurularınızı ve kampanyalarınızı ziyaretçinin ilk gördüğü
                alanlarda sergileyin.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-xs font-bold text-zinc-600 shadow-sm">
                <Store className="h-4 w-4 text-[#EA580C]" />
                Sektör bağımsız yapı
              </div>

              <h2 className="mt-5 text-3xl font-extrabold tracking-[-0.035em] text-zinc-950 sm:text-5xl">
                Menüden fazlası. İşletmenizin dijital vitrini.
              </h2>

              <p className="mt-5 max-w-xl text-base font-medium leading-8 text-zinc-600">
                Web Menü’yü yalnızca restoranlar için değil, ürününü veya
                hizmetini dijitalde düzenli ve profesyonel göstermek isteyen
                işletmeler için tasarlıyoruz.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {[
                ["Kafe & Restoran", "Ürün, kategori ve kampanyalar"],
                ["Mobilya & Dekorasyon", "Koleksiyon ve hizmet vitrini"],
                ["Güzellik & Bakım", "Hizmet ve fiyat listeleri"],
                ["Oto & Servis", "Hizmet paketleri ve kampanyalar"],
                ["Üretici & Atölye", "Ürün grupları ve katalog"],
                ["Mağaza & Perakende", "Ürün seçkileri ve duyurular"],
              ].map(([title, desc], index) => (
                <article
                  key={title}
                  className="group rounded-[24px] border border-zinc-200 bg-[#FCFBF9] p-5 transition hover:-translate-y-1 hover:border-orange-200 hover:bg-white hover:shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black tracking-[0.16em] text-zinc-300">
                      0{index + 1}
                    </span>
                    <span className="h-2 w-2 rounded-full bg-orange-400 opacity-70" />
                  </div>

                  <h3 className="mt-5 text-base font-extrabold text-zinc-950">
                    {title}
                  </h3>

                  <p className="mt-2 text-sm font-medium leading-6 text-zinc-500">
                    {desc}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="scroll-mt-24 bg-zinc-950 py-20 text-white sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold text-zinc-300">
              <Wallet className="h-4 w-4 text-orange-300" />
              Tek ürün, üç ödeme süresi
            </div>

            <h2 className="mt-5 text-3xl font-extrabold tracking-[-0.035em] text-white sm:text-5xl">
              Tüm özellikler dahil. Sadece kullanım sürenizi seçin.
            </h2>

            <p className="mt-5 text-base font-medium leading-8 text-zinc-400">
              Özellik kısıtlaması veya Pro paketi yok. 1, 6 veya 12 aylık
              aboneliklerden size uygun olanı seçin.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-6xl gap-5 lg:grid-cols-3">
            {[
              {
                name: "1 Aylık",
                price: "849,99 ₺",
                period: "1 aylık kullanım",
                monthly: "849,99 ₺ / ay",
                note: "Kısa süreli kullanım",
              },
              {
                name: "6 Aylık",
                price: "4.499,99 ₺",
                period: "6 aylık kullanım",
                monthly: "≈ 750 ₺ / ay",
                note: "Yaklaşık 600 ₺ avantaj",
                badge: "Avantajlı",
              },
              {
                name: "12 Aylık",
                price: "7.999,99 ₺",
                period: "12 aylık kullanım",
                monthly: "≈ 667 ₺ / ay",
                note: "Yaklaşık 2.200 ₺ avantaj",
                badge: "En Avantajlı",
                featured: true,
              },
            ].map((plan) => (
              <article
                key={plan.name}
                className={`relative flex min-h-[430px] flex-col rounded-[30px] border p-7 transition ${
                  plan.featured
                    ? "border-orange-400 bg-white text-zinc-950 shadow-[0_25px_80px_rgba(234,88,12,0.18)]"
                    : "border-white/10 bg-white/[0.04] text-white hover:border-white/20 hover:bg-white/[0.06]"
                }`}
              >
                {plan.badge && (
                  <div
                    className={`mb-6 inline-flex w-fit rounded-full px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.14em] ${
                      plan.featured
                        ? "bg-orange-100 text-orange-700"
                        : "bg-white/10 text-orange-200"
                    }`}
                  >
                    {plan.badge}
                  </div>
                )}

                <div>
                  <p
                    className={`text-sm font-bold ${
                      plan.featured ? "text-zinc-500" : "text-zinc-400"
                    }`}
                  >
                    {plan.name}
                  </p>

                  <div className="mt-4 text-[42px] font-black leading-none tracking-[-0.045em] sm:text-5xl">
                    {plan.price}
                  </div>

                  <p
                    className={`mt-3 text-sm font-medium ${
                      plan.featured ? "text-zinc-500" : "text-zinc-400"
                    }`}
                  >
                    {plan.period}
                  </p>
                </div>

                <div
                  className={`mt-8 rounded-2xl p-4 ${
                    plan.featured ? "bg-orange-50" : "bg-white/5"
                  }`}
                >
                  <p
                    className={`text-[10px] font-bold uppercase tracking-[0.15em] ${
                      plan.featured ? "text-orange-600" : "text-zinc-500"
                    }`}
                  >
                    Aylık karşılığı
                  </p>

                  <p className="mt-2 text-xl font-extrabold">
                    {plan.monthly}
                  </p>
                </div>

                <div className="mt-6 space-y-3 text-sm font-semibold">
                  {[
                    "Tüm Web Menü özellikleri",
                    "QR kod ve özel bağlantı",
                    "Ürün, hizmet ve kampanya yönetimi",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <span
                        className={`grid h-5 w-5 place-items-center rounded-full text-[10px] font-black ${
                          plan.featured
                            ? "bg-zinc-950 text-white"
                            : "bg-white/10 text-orange-200"
                        }`}
                      >
                        ✓
                      </span>
                      <span
                        className={
                          plan.featured ? "text-zinc-700" : "text-zinc-300"
                        }
                      >
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto pt-8">
                  <p
                    className={`mb-4 text-xs font-bold ${
                      plan.featured ? "text-orange-700" : "text-orange-200"
                    }`}
                  >
                    {plan.note}
                  </p>

                  <Button
                    onClick={() => navigate("/register")}
                    className={`h-12 w-full rounded-full font-bold ${
                      plan.featured
                        ? "bg-[#EA580C] text-white hover:bg-[#C2410C]"
                        : "bg-white text-zinc-950 hover:bg-zinc-100"
                    }`}
                  >
                    Hesabını Oluştur
                  </Button>
                </div>
              </article>
            ))}
          </div>

          <div className="mx-auto mt-8 max-w-4xl rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 text-center text-xs font-medium leading-6 text-zinc-400">
            Hangi abonelik süresini seçerseniz seçin mevcut Web Menü
            özelliklerinin tamamına erişebilirsiniz. Paketler yalnızca kullanım
            süresine göre farklılık gösterir.
          </div>
        </div>
      </section>

      <section id="mobile-app" className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 overflow-hidden rounded-[36px] border border-zinc-200 bg-[#F7F6F3] p-6 sm:p-8 lg:grid-cols-[0.82fr_1.18fr] lg:p-10 xl:p-12">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-xs font-bold text-zinc-600 shadow-sm">
                <Smartphone className="h-4 w-4 text-[#EA580C]" />
                Web + mobil, aynı işletme deneyimi
              </div>

              <h2 className="mt-5 text-3xl font-extrabold tracking-[-0.035em] text-zinc-950 sm:text-5xl">
                Web Menü’nüz masada değil, her zaman yanınızda.
              </h2>

              <p className="mt-5 max-w-xl text-base font-medium leading-8 text-zinc-600">
                İşletme içeriklerinizi web panelinden veya mobil uygulamadan
                yönetin. Ürün, hizmet, kampanya ve vitrin bilgileriniz aynı
                işletme hesabında güncel kalsın.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {["İçeriğini dilediğin yerden yönet", "Web ve mobilde aynı işletme hesabı", "Kampanyaları hızlıca güncelle", "QR ve bağlantını her zaman hazır tut"].map(
                  (item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-bold text-zinc-700"
                    >
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-orange-50 text-[10px] font-black text-[#EA580C]">
                        ✓
                      </span>
                      {item}
                    </div>
                  ),
                )}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => navigate("/register")}
                  className="h-12 rounded-full bg-zinc-950 px-6 font-bold text-white hover:bg-zinc-800"
                >
                  Hesabını Oluştur
                </Button>

                <span className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold text-orange-700">
                  iOS uygulaması yayına hazırlanıyor
                </span>
              </div>
            </div>

            <div className="relative min-h-[520px] sm:min-h-[560px]">
              <div
                aria-hidden="true"
                className="absolute left-1/2 top-1/2 h-[70%] w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-200/35 blur-[80px]"
              />

              <div className="absolute left-0 top-12 hidden w-[72%] overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-[0_30px_80px_rgba(24,24,27,0.10)] sm:block">
                <div className="flex h-12 items-center justify-between border-b border-zinc-100 px-4">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                    <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                    <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  </div>
                  <span className="text-[9px] font-bold text-zinc-400">
                    Web Menü Yönetim Paneli
                  </span>
                </div>

                <div className="grid min-h-[360px] grid-cols-[0.28fr_0.72fr]">
                  <div className="border-r border-zinc-100 bg-zinc-950 p-4 text-white">
                    <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#EA580C] text-[10px] font-black">
                      WM
                    </div>
                    <div className="mt-6 space-y-2">
                      {["Genel Bakış", "İçerikler", "Kampanyalar", "QR Kod", "Ayarlar"].map(
                        (item, index) => (
                          <div
                            key={item}
                            className={`rounded-lg px-3 py-2 text-[9px] font-bold ${
                              index === 0
                                ? "bg-white/10 text-white"
                                : "text-zinc-500"
                            }`}
                          >
                            {item}
                          </div>
                        ),
                      )}
                    </div>
                  </div>

                  <div className="bg-[#FAFAF9] p-5">
                    <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-400">
                      İşletmen
                    </p>
                    <p className="mt-1 text-lg font-extrabold text-zinc-950">
                      Arven Concept
                    </p>

                    <div className="mt-5 grid grid-cols-3 gap-2">
                      {["24 Ürün", "6 Hizmet", "3 Kampanya"].map((item) => (
                        <div
                          key={item}
                          className="rounded-xl border border-zinc-200 bg-white px-3 py-4 text-center text-[9px] font-extrabold text-zinc-700"
                        >
                          {item}
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[9px] font-bold text-zinc-400">
                            Web Menü durumu
                          </p>
                          <p className="mt-1 text-xs font-extrabold text-zinc-900">
                            Yayında ve güncel
                          </p>
                        </div>
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute bottom-0 left-1/2 w-[255px] -translate-x-1/2 rounded-[42px] bg-zinc-950 p-[7px] shadow-[0_35px_80px_rgba(24,24,27,0.24)] sm:left-auto sm:right-2 sm:translate-x-0 lg:right-4">
                <div className="overflow-hidden rounded-[35px] bg-[#F8F7F4]">
                  <div className="flex h-8 items-center justify-center bg-zinc-950">
                    <div className="h-1.5 w-14 rounded-full bg-zinc-700" />
                  </div>

                  <div className="px-4 pb-5 pt-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[8px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                          Web Menü
                        </p>
                        <p className="mt-1 text-sm font-extrabold text-zinc-950">
                          Günaydın, Arven 👋
                        </p>
                      </div>
                      <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#EA580C] text-[9px] font-black text-white">
                        AC
                      </div>
                    </div>

                    <div className="mt-5 rounded-2xl bg-zinc-950 p-4 text-white">
                      <p className="text-[8px] font-bold uppercase tracking-[0.15em] text-orange-300">
                        Web Menü’n
                      </p>
                      <p className="mt-2 text-sm font-extrabold">Yayında</p>
                      <p className="mt-1 text-[9px] leading-4 text-zinc-400">
                        İçeriklerin müşterilerin tarafından görüntülenebilir.
                      </p>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      {["Ürün Ekle", "Kampanya", "QR Kod", "Ön İzleme"].map(
                        (item, index) => (
                          <div
                            key={item}
                            className={`rounded-xl border p-3 text-[9px] font-extrabold ${
                              index === 0
                                ? "border-orange-100 bg-orange-50 text-orange-700"
                                : "border-zinc-200 bg-white text-zinc-700"
                            }`}
                          >
                            <span className="mb-3 block text-[8px] text-zinc-300">
                              0{index + 1}
                            </span>
                            {item}
                          </div>
                        ),
                      )}
                    </div>

                    <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-3">
                      <p className="text-[8px] font-bold text-zinc-400">
                        Son güncelleme
                      </p>
                      <p className="mt-1 text-[10px] font-extrabold text-zinc-800">
                        Vitrin içeriği güncel
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute right-0 top-2 hidden rounded-2xl border border-zinc-200 bg-white p-3 shadow-xl xl:block">
                <div className="flex items-center gap-3">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-orange-50 text-[#EA580C]">
                    <Smartphone className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[8px] font-bold uppercase tracking-[0.13em] text-zinc-400">
                      Aynı hesap
                    </p>
                    <p className="mt-1 text-[10px] font-extrabold text-zinc-800">
                      Web + mobil
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FCFBF9] py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[34px] border border-orange-100 bg-white px-6 py-12 shadow-[0_30px_90px_rgba(24,24,27,0.08)] sm:px-10 lg:px-14">
            <div
              aria-hidden="true"
              className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-orange-100 blur-[60px]"
            />

            <div className="relative grid items-center gap-8 lg:grid-cols-[1fr_auto]">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#EA580C]">
                  Web Menü
                </p>

                <h2
                  className="mt-3 max-w-3xl text-3xl font-extrabold tracking-[-0.035em] text-zinc-950 sm:text-5xl"
                  data-testid="cta-heading"
                >
                  İşletmenizin dijital vitrini bugün hazır olsun.
                </h2>

                <p className="mt-4 max-w-2xl text-base font-medium leading-8 text-zinc-600">
                  Hesabınızı oluşturun, ürün ve hizmetlerinizi ekleyin, Web Menü
                  bağlantınızı müşterilerinizle paylaşmaya başlayın.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <Button
                  size="lg"
                  onClick={() => navigate("/register")}
                  className="h-12 rounded-full bg-[#EA580C] px-8 font-bold text-white hover:bg-[#C2410C]"
                  data-testid="cta-create-menu-button"
                >
                  Web Menü’nü Oluştur
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  onClick={openWhatsapp}
                  className="h-12 rounded-full border-zinc-300 bg-white px-8 font-bold text-zinc-800 hover:bg-zinc-50"
                >
                  Bilgi Al
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 bg-zinc-950 text-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-14">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.35fr_0.75fr_0.75fr_1fr]">
            <div>
              <button
                type="button"
                onClick={() => scrollToSection("top")}
                className="flex items-center gap-3 text-left"
              >
                <div className="grid h-11 w-11 place-items-center rounded-[14px] bg-[#EA580C] text-white">
                  <Menu className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-base font-extrabold tracking-[-0.02em]">
                    Web Menü
                  </p>
                  <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">
                    Megriva teknolojisiyle
                  </p>
                </div>
              </button>

              <p className="mt-5 max-w-sm text-sm font-medium leading-7 text-zinc-400">
                Ürünlerinizi, hizmetlerinizi ve kampanyalarınızı tek bağlantıda
                profesyonel bir dijital vitrine dönüştürün.
              </p>

              <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold text-zinc-400">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                Web Menü geliştirmeleri aktif olarak devam ediyor
              </div>
            </div>

            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-zinc-500">
                Ürün
              </p>
              <div className="mt-5 grid gap-3 text-sm font-semibold text-zinc-300">
                <button
                  type="button"
                  onClick={() => scrollToSection("how-it-works")}
                  className="w-fit transition hover:text-white"
                >
                  Nasıl Çalışır
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection("features")}
                  className="w-fit transition hover:text-white"
                >
                  Özellikler
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection("pricing")}
                  className="w-fit transition hover:text-white"
                >
                  Fiyatlar
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection("mobile-app")}
                  className="w-fit transition hover:text-white"
                >
                  Mobil Uygulama
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-zinc-500">
                Hesap
              </p>
              <div className="mt-5 grid gap-3 text-sm font-semibold text-zinc-300">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="w-fit transition hover:text-white"
                >
                  Giriş Yap
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  className="w-fit transition hover:text-white"
                >
                  Hesap Oluştur
                </button>
                <button
                  type="button"
                  onClick={openWhatsapp}
                  className="w-fit transition hover:text-white"
                >
                  Bilgi Al
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-zinc-500">
                Web Menü
              </p>
              <p className="mt-5 text-sm font-medium leading-7 text-zinc-400">
                İşletmenizin dijital vitrini için sade, hızlı ve yönetilebilir
                bir deneyim.
              </p>

              <a
                href="https://megriva.com"
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-orange-300 transition hover:text-orange-200"
              >
                Megriva’yı ziyaret et
                <span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs font-medium text-zinc-500 sm:flex-row sm:items-center sm:justify-between">
            <p>© {new Date().getFullYear()} Web Menü. Tüm hakları saklıdır.</p>
            <div className="flex items-center gap-4">
              <a href="/privacy_policy" className="transition hover:text-zinc-300">
                Gizlilik Politikası
              </a>
              <a href="/terms_of_use" className="transition hover:text-zinc-300">
                Kullanım Koşulları
              </a>
              <span>Powered by Megriva</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}